import { recordPage } from '@avrahami/ui';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { aiCopyFilterSchema, domainStatusSchema } from '@avrahami/contracts';
import { z } from 'zod';
import { AppLayout } from './components/Layout';
import { AccountPage } from './pages/AccountPage';
import { AiPage } from './pages/AiPage';
import { AuditPage } from './pages/AuditPage';
import { CatalogPage } from './pages/CatalogPage';
import { DomainsPage } from './pages/DomainsPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { InvitationPage } from './pages/InvitationPage';
import { LoginPage } from './pages/LoginPage';
import { MfaSetupPage } from './pages/MfaSetupPage';
import { NoAccessPage } from './pages/NoAccessPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ServicePage } from './pages/ServicePage';
import { StaffPage } from './pages/StaffPage';
import { TenantContactPage } from './pages/TenantContactPage';
import { TenantCopyPage } from './pages/TenantCopyPage';
import { TenantCreatePage } from './pages/TenantCreatePage';
import { TenantDesignPage } from './pages/TenantDesignPage';
import { TenantDetailPage } from './pages/TenantDetailPage';
import { TenantDomainsPage } from './pages/TenantDomainsPage';
import { TenantProductPage } from './pages/TenantProductPage';
import { TenantServicePage } from './pages/TenantServicePage';
import { TenantsPage } from './pages/TenantsPage';

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: Outlet,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: z.object({ redirect: z.string().max(2048).optional() }),
  component: LoginPage,
});

const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitation',
  component: InvitationPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

/** The mailed "forgot password" link opens here (adr/0035). */
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});

const noAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/no-access',
  component: NoAccessPage,
});

const mfaSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mfa/setup',
  component: MfaSetupPage,
});

/** Everything behind sign-in, a platform role and a second factor (checked in AppLayout). */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- the router's redirect idiom
    throw redirect({ to: '/tenants' });
  },
});

const tenantsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tenants',
  component: TenantsPage,
});

const tenantCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tenants/new',
  component: TenantCreatePage,
});

const tenantDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tenants/$tenantId',
  component: TenantDetailPage,
});

/**
 * One store's marketing copy (`platform.ai.manage`, checked on the page); a link may open it on
 * a filter and a search.
 */
const tenantCopyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tenants/$tenantId/copy',
  validateSearch: z.object({
    filter: aiCopyFilterSchema.optional().catch(undefined),
    q: z.string().trim().min(1).max(100).optional().catch(undefined),
  }),
  component: TenantCopyPage,
});

/** One catalog product's page in one store (`platform.ai.manage`, checked on the page; adr/0043). */
const tenantProductRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tenants/$tenantId/products/$productId',
  component: TenantProductPage,
});

/** One store's addresses: the platform staff connect a store's own domains (adr/0025). */
const tenantDomainsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tenants/$tenantId/domains',
  component: TenantDomainsPage,
});

/** One store's WhatsApp number and mail account, set by the platform staff (adr/0028). */
const tenantContactRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tenants/$tenantId/contact',
  component: TenantContactPage,
});

/** One store's design and opening banner, set by the platform staff (adr/0034). */
const tenantDesignRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tenants/$tenantId/design',
  component: TenantDesignPage,
});

/**
 * One store's faults and service calls (adr/0036); a link may open it on one ticket, and with
 * the resolved ones too.
 */
const tenantServiceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tenants/$tenantId/service',
  validateSearch: z.object({
    ticket: z.uuid().optional().catch(undefined),
    all: z.boolean().optional().catch(undefined),
  }),
  component: TenantServicePage,
});

/** The faults and calls still open in every store. */
const serviceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/service',
  component: ServicePage,
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit',
  validateSearch: z.object({ tenantId: z.uuid().optional().catch(undefined) }),
  component: AuditPage,
});

const staffRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/staff',
  component: StaffPage,
});

const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account',
  component: AccountPage,
});

const catalogRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/catalog',
  component: CatalogPage,
});

const aiRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ai',
  component: AiPage,
});

/** A link may open the list on one status; anything else in the address is ignored. */
const domainsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/domains',
  validateSearch: z.object({ status: domainStatusSchema.optional().catch(undefined) }),
  component: DomainsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  invitationRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  noAccessRoute,
  mfaSetupRoute,
  appRoute.addChildren([
    indexRoute,
    tenantsRoute,
    tenantCreateRoute,
    tenantDetailRoute,
    tenantCopyRoute,
    tenantProductRoute,
    tenantDomainsRoute,
    tenantContactRoute,
    tenantDesignRoute,
    tenantServiceRoute,
    serviceRoute,
    auditRoute,
    staffRoute,
    accountRoute,
    catalogRoute,
    aiRoute,
    domainsRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  const router = createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // Going back returns to the same place: the scroll position of every history entry.
    scrollRestoration: true,
  });
  // "Back to the list" links go back in history when the list is where the person came from.
  router.subscribe('onResolved', recordPage);
  return router;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
